import { authWebSocket } from '../auth/authFetches';
import { generatedTextToHtml } from '@/utils/textFormatting';
import { PROPOSALACTIONS } from '../pages/EditProposal/reducers/proposalReducer';
import { USERFEEDBACKACTIONS } from '../pages/EditProposal/reducers/userFeedbackReducer';
import { OTHERDATAACTIONS } from '../pages/EditProposal/reducers/otherDataReducer';
import { Proposal } from '../interfaces/types';
import { notifications } from "@mantine/notifications"

export type Field = 'scope' | 'assumptions' | 'lawyers' | 'experiences';

export type GenerationConfig = {
    input: string;
    sector?: string;
    documentLanguage: string;
    constraints?: {
        teamComposition?: Array<{ team: string; quantity: number }>;
        nExperiences?: number;
        requiredLawyers?: string[];
    };
    proposal: Proposal;
    sort?: boolean
};

export type GenerationHandlers = {
    dispatchProposal: React.Dispatch<any>;
    dispatchUserFeedback: React.Dispatch<any>;
    dispatchOtherData: React.Dispatch<any>;
};

export class GenerationService {
    private static instance: GenerationService | null = null;
    private ws: WebSocket | null = null;
    private connectionPromise: Promise<WebSocket> | null = null;
    private messageHandlers: Map<string, Map<string, (data: any) => void>> = new Map();
    private messageDefaultHandlers: Map<string, (data: any) => void> = new Map();
    private operations: Partial<Record<Field, { cancel: () => void }>> = {};

    private constructor(private accessToken: string) { }

    public static getInstance(accessToken: string) {
        if (
            !GenerationService.instance ||
            GenerationService.instance.accessToken !== accessToken
        ) {
            GenerationService.instance?.close();
            GenerationService.instance = new GenerationService(accessToken);
        }
        return GenerationService.instance;
    }

    private static sanitizeLLMErrorMessage(rawError: unknown, language: string): string {
        const raw = typeof rawError === 'string' ? rawError : String(rawError ?? '');
        if (/No XML tags returned/i.test(raw)) {
            return language === 'pl' ? 'Brak znaczników XML w odpowiedzi.' : 'No XML tags returned.';
        }
        const beforeResponse = raw.split(/Response\s*:/i)[0] || raw;
        const stripped = beforeResponse.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        if (stripped.length === 0) {
            return language === 'pl' ? 'Wystąpił błąd generowania treści.' : 'A generation error occurred.';
        }
        return stripped.slice(0, 400);
    }

    private async getWebSocket(): Promise<WebSocket> {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return this.ws;
        }

        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            this.ws = authWebSocket(this.accessToken);

            this.ws.onopen = () => {
                console.log('WebSocket connection established');
                resolve(this.ws!);
            };

            this.ws.onerror = (error) => {
                console.error('WebSocket connection error:', error);
                this.ws = null;
                this.connectionPromise = null;
                reject(error);
            };

            this.ws.onclose = () => {
                console.log('WebSocket connection closed');
                this.ws = null;
                this.connectionPromise = null;
                this.messageHandlers.clear();
                this.messageDefaultHandlers.clear();
            };

            this.ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                const handler = this.messageHandlers.get(data.source)?.get(data.type);
                if (handler) {
                    handler(data);
                } else {
                    const defaultHandler = this.messageDefaultHandlers.get(data.source)
                    if (defaultHandler) {
                        defaultHandler(data);
                    }
                }
            };
        });

        return this.connectionPromise;
    }

    private registerMessageHandler(source: string, type: string, handler: (data: any) => void) {
        if (!this.messageHandlers.has(source)) {
            this.messageHandlers.set(source, new Map());
        }
        this.messageHandlers.get(source)?.set(type, handler);
    }

    private registerDefaultMessageHandler(source: string, handler: (data: any) => void) {
        this.messageDefaultHandlers.set(source, handler);
    }

    public cancelField(field: Field) {
        const op = this.operations[field];
        if (!op) return;
        op.cancel();
        delete this.operations[field];
    }

    public cancelAll() {
        (Object.keys(this.operations) as Field[]).forEach((f) => this.cancelField(f));
    }

    public async generateScope(config: GenerationConfig, handlers: GenerationHandlers): Promise<void> {
        this.cancelField('scope');

        let wsRef: WebSocket | null = null;
        const { dispatchProposal, dispatchUserFeedback } = handlers;

        const promise = new Promise<void>((resolve) => {
            const handler = (data: any) => {
                const raw = data.data as string;
                const value = (typeof raw === 'string' && raw.includes('<') && raw.includes('>'))
                    ? raw
                    : generatedTextToHtml(String(raw || ''));
                dispatchProposal({
                    type: PROPOSALACTIONS.SET_FIELD,
                    field: 'scope',
                    value,
                });
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingScope', value: false });
                resolve();
                this.messageHandlers.get('generate_scope')?.delete('scope_response');
            };
            this.registerMessageHandler('generate_scope', 'scope_response', handler);

            this.operations.scope = {
                cancel: () => {
                    this.messageHandlers.get('generate_scope')?.delete('scope_response');
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingScope', value: false });
                },
            };
        });

        try {
            const ws = await this.getWebSocket();
            wsRef = ws;
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingScope', value: true });
            ws.send(
                JSON.stringify({
                    action: 'generateScope',
                    language: config.documentLanguage === 'pl' ? 'Polish' : 'English',
                    query: config.input,
                })
            );
        } catch (error) {
            console.error('Error generating scope:', error);
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingScope', value: false });
        }

        return promise;
    }

    public async generateAssumptions(config: GenerationConfig, handlers: GenerationHandlers): Promise<void> {
        this.cancelField('assumptions');

        let wsRef: WebSocket | null = null;
        const { dispatchProposal, dispatchUserFeedback } = handlers;

        const promise = new Promise<void>((resolve) => {
            const handler = (data: any) => {
                const raw = data.data as string;
                const value = (typeof raw === 'string' && raw.includes('<') && raw.includes('>'))
                    ? raw
                    : generatedTextToHtml(String(raw || ''));
                dispatchProposal({
                    type: PROPOSALACTIONS.SET_FIELD,
                    field: 'assumptions',
                    value,
                });
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingAssumptions', value: false });
                resolve();
                this.messageHandlers.get('generate_assumptions')?.delete('assumptions_response');
            };
            this.registerMessageHandler('generate_assumptions', 'assumptions_response', handler);

            this.operations.assumptions = {
                cancel: () => {
                    this.messageHandlers.get('generate_assumptions')?.delete('assumptions_response');
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingAssumptions', value: false });
                },
            };
        });

        try {
            const ws = await this.getWebSocket();
            wsRef = ws;
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingAssumptions', value: true });
            ws.send(
                JSON.stringify({
                    action: 'generateAssumptions',
                    language: config.documentLanguage === 'pl' ? 'Polish' : 'English',
                    query: config.input,
                })
            );
        } catch (error) {
            console.error('Error generating assumptions:', error);
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingAssumptions', value: false });
        }

        return promise;
    }

    public async generateLawyers(config: GenerationConfig, handlers: GenerationHandlers): Promise<void> {
        this.cancelField('lawyers');

        const { input, sector, documentLanguage, constraints, proposal } = config;
        const { dispatchProposal, dispatchUserFeedback } = handlers;

        if (!(constraints?.requiredLawyers?.length || constraints?.teamComposition?.length)) {
            return Promise.resolve();
        }

        let wsRef: WebSocket | null = null;
        let pendingCompletions = (constraints?.teamComposition?.length || 0)

        const promise = new Promise<void>((resolve) => {
            this.registerMessageHandler('offer_creation_lawyers', 'selected_lawyers', (data) => {
                const lawyerNames = data.data;
                lawyerNames.forEach((lawyerName: string) => {
                    if (!proposal.lawyers.map((lawyer: any) => lawyer.name).includes(lawyerName)) {
                        dispatchProposal({
                            type: PROPOSALACTIONS.ADD_LAWYER,
                            payload: { bio: '', name: lawyerName },
                        });
                        dispatchUserFeedback({
                            type: USERFEEDBACKACTIONS.ADD_TO_LOADING_LAWYERS_LIST,
                            value: lawyerName,
                        });
                    }
                });
            });

            this.registerMessageHandler('offer_creation_lawyers', 'bio_response', (data) => {
                const { lawyer, bio } = data.data;
                dispatchProposal({
                    type: PROPOSALACTIONS.EDIT_LAWYER_BIO,
                    payload: { name: lawyer, bio },
                });
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.REMOVE_FROM_LOADING_LAWYERS_LIST, value: lawyer });
            });

            this.registerMessageHandler('offer_creation_lawyers', 'completed', (data) => {
                if (data.source === 'offer_creation_lawyers') {
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.INCREMENT_CURRENT_LAWYER_COMPLETIONS });
                    pendingCompletions--;
                    if (data.error) {
                        const msg = GenerationService.sanitizeLLMErrorMessage(data.error, proposal.language);
                        notifications.show({
                            title: proposal.language === 'pl' ? 'Błąd' : 'Error',
                            message: msg,
                            color: 'red',
                            autoClose: 3000,
                            position: 'top-right',
                        });
                    }
                    if (pendingCompletions <= 0) {
                        dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingLawyers', value: false });
                        resolve();
                    }
                }
            });

            this.registerDefaultMessageHandler("offer_creation_lawyers", (data) => {
                dispatchUserFeedback({
                    type: USERFEEDBACKACTIONS.SET_FIELD,
                    field: "lawyerLoadingState",
                    value: data.type
                });
            });

            // For the bio generation of the required lawyers
            this.registerMessageHandler('lawyer_bio_generation', 'bio_response', (data) => {
                const { lawyer, bio } = data.data;
                dispatchProposal({
                    type: PROPOSALACTIONS.EDIT_LAWYER_BIO,
                    payload: { name: lawyer, bio },
                });
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.REMOVE_FROM_LOADING_LAWYERS_LIST, value: lawyer });
                resolve();
            });

            this.operations.lawyers = {
                cancel: () => {
                    const sourceMap = this.messageHandlers.get('offer_creation_lawyers');
                    if (sourceMap) {
                        sourceMap.delete('selected_lawyers');
                        sourceMap.delete('bio_response');
                        sourceMap.delete('completed');
                        sourceMap.delete('lawyer_bio_generation');
                    }
                    this.messageDefaultHandlers.delete('offer_creation_lawyers');
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingLawyers', value: false });
                },
            };
        });

        try {
            const ws = await this.getWebSocket();
            wsRef = ws;
            if (pendingCompletions > 0) {
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingLawyers', value: true });
            }

            if (constraints?.requiredLawyers?.length) {
                for (let lawyerName of constraints.requiredLawyers) {
                    if (!proposal.lawyers.map((lawyer) => lawyer.name).includes(lawyerName)) {
                        dispatchProposal({
                            type: PROPOSALACTIONS.ADD_LAWYER,
                            payload: { name: lawyerName, bio: '' },
                        });
                    }
                    dispatchUserFeedback({
                        type: USERFEEDBACKACTIONS.ADD_TO_LOADING_LAWYERS_LIST,
                        value: lawyerName,
                    });
                    ws.send(
                        JSON.stringify({
                            action: 'generateLawyers',
                            lawyer_names: [lawyerName],
                            language: documentLanguage,
                            query: input,
                        })
                    );
                }
            }

            if (constraints?.teamComposition?.length) {
                for (const item of constraints.teamComposition) {
                    ws.send(
                        JSON.stringify({
                            action: 'generateLawyers',
                            query: input.concat('Client Sector: ', sector || ''),
                            language: documentLanguage,
                            team_constraint: item.team === 'Any' ? '' : item.team,
                            top_k: item.quantity,
                        })
                    );
                }
            }
        } catch (error) {
            console.error('Error generating lawyers:', error);
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingLawyers', value: false });
        }

        return promise;
    }

    public async generateExperiences(config: GenerationConfig, handlers: GenerationHandlers): Promise<void> {
        this.cancelField('experiences');

        const { input, documentLanguage, constraints } = config;
        const { dispatchProposal, dispatchUserFeedback } = handlers;

        if (!constraints || !constraints.nExperiences) {
            return Promise.resolve();
        }

        let wsRef: WebSocket | null = null;

        const promise = new Promise<void>((resolve) => {
            this.registerMessageHandler('offer_creation_experiences', 'selected_experiences', (data) => {
                for (const experience of data.data) {
                    dispatchProposal({
                        type: PROPOSALACTIONS.ADD_EXPERIENCE,
                        payload: experience,
                    });
                }
            });

            this.registerMessageHandler('offer_creation_experiences', 'completed', (data) => {
                if (data.source === 'offer_creation_experiences') {
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingExperiences', value: false });
                    resolve();
                }
            });

            this.registerDefaultMessageHandler("offer_creation_experiences", (data) => {
                dispatchUserFeedback({
                    type: USERFEEDBACKACTIONS.SET_FIELD,
                    field: "experienceLoadingState",
                    value: data.type
                });
            });

            this.operations.experiences = {
                cancel: () => {
                    const sourceMap = this.messageHandlers.get('offer_creation_experiences');
                    if (sourceMap) {
                        sourceMap.delete('selected_experiences');
                        sourceMap.delete('completed');
                    }
                    this.messageDefaultHandlers.delete('offer_creation_experiences');
                    dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingExperiences', value: false });
                },
            };
        });

        try {
            const ws = await this.getWebSocket();
            wsRef = ws;
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingExperiences', value: true });
            ws.send(
                JSON.stringify({
                    action: 'generateExperiences',
                    query: input,
                    language: documentLanguage,
                    top_k: constraints.nExperiences,
                })
            );
        } catch (error) {
            console.error('Error generating experiences:', error);
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: 'loadingExperiences', value: false });
        }

        return promise;
    }

    public async generateBio(config: { input: string, documentLanguage: string, lawyerName: string }, handlers: GenerationHandlers): Promise<void> {
        const { input, documentLanguage, lawyerName } = config;
        const { dispatchProposal, dispatchUserFeedback } = handlers;

        let wsRef: WebSocket | null = null;

        const promise = new Promise<void>((resolve) => {
            // Note that, technically, if you have called generateBio multiple times, the last call will override the previous ones - but this is ok because this listener is agnostic. 
            // This is a quick fix 
            this.registerMessageHandler('lawyer_bio_generation', 'bio_response', (data) => {
                const { lawyer, bio } = data.data;
                dispatchProposal({
                    type: PROPOSALACTIONS.EDIT_LAWYER_BIO,
                    payload: { name: lawyer, bio },
                });
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.REMOVE_FROM_LOADING_LAWYERS_LIST, value: lawyer });
                resolve();
            });

            // Set a timeout to remove from loading list after 15 seconds
            setTimeout(() => {
                dispatchUserFeedback({ type: USERFEEDBACKACTIONS.REMOVE_FROM_LOADING_LAWYERS_LIST, value: lawyerName });
            }, 15000);
        });

        try {
            const ws = await this.getWebSocket();
            wsRef = ws;
            dispatchUserFeedback({
                type: USERFEEDBACKACTIONS.ADD_TO_LOADING_LAWYERS_LIST,
                value: lawyerName,
            });
            ws.send(
                JSON.stringify({
                    action: 'generateLawyers',
                    lawyer_names: [lawyerName],
                    language: documentLanguage,
                    query: input,
                })
            );
        } catch (error) {
            console.error('Error generating bio:', error);
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.REMOVE_FROM_LOADING_LAWYERS_LIST, value: lawyerName });
        }

        return promise;
    }

    public close() {
        console.log('GenerationService: close() called');
        this.cancelAll();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
            this.connectionPromise = null;
            this.messageHandlers.clear();
            this.messageDefaultHandlers.clear();
        }
    }
} 